// src/pages/InvitedClubPage/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { FaPlus, FaTrash, FaShareSquare } from "react-icons/fa";
import Button from "../../components/Button/Button";
import { useUser } from "../../contexts/UserContext";
import useScrollToTop from "../../hooks/useScrollToTop";

import {
  getInvitedClubsByUser,
  createInvitedClub,
  updateInvitedClub,
  deleteInvitedClub,
  bulkDeleteInvitedClubs,
} from "../../services/invitedClubsServices";

import { getUserCompetitions } from "../../services/competitionsServices";
import {
  assignClubsToCompetition,
  listInvitesByCompetition,
  deleteInvite,
} from "../../services/competitionClubInvitesServices";

import InvitedClubTable from "./components/InvitedClubTable";
import CreateInvitedClubModal from "./components/CreateInvitedClubModal";
import EditInvitedClubModal from "./components/EditInvitedClubModal";
import BulkAssignModal from "./components/BulkAssignModal";
import ManageClubCompetitionsModal from "./components/ManageClubCompetitionsModal";

const InvitedClubPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userId = user?.userId;

  // liste brute (tous les clubs du user)
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // filtres / pagination (client)
  const [search, setSearch] = useState("");
  const [onlyWithEmail, setOnlyWithEmail] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // sélection
  const [selectedIds, setSelectedIds] = useState([]);

  // modales
  const [createOpen, setCreateOpen] = useState(false);
  const [editClub, setEditClub] = useState(null);
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [manageClub, setManageClub] = useState(null);

  // compétitions de l’organisateur
  const [competitions, setCompetitions] = useState([]);

  const fetchCompetitions = async () => {
    try {
      const data = await getUserCompetitions(userId);
      setCompetitions(data || []);
    } catch {
      setCompetitions([]);
    }
  };

  const fetchClubs = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const rows = await getInvitedClubsByUser(userId); // ← récupère TOUT
      setItems(Array.isArray(rows) ? rows : []);
      // on garde la page courante mais on clamp si besoin
      setPage(1);
    } catch {
      toast.error("Erreur lors du chargement des clubs invités.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userId) return;
    fetchCompetitions();
    fetchClubs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // ======= Filtres (client) =======
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = items;

    if (q) {
      rows = rows.filter((r) => {
        const n = r.nom_club?.toLowerCase() || "";
        const m = r.mail_club?.toLowerCase() || "";
        const t = r.tel_club?.toLowerCase() || "";
        return n.includes(q) || m.includes(q) || t.includes(q);
      });
    }
    if (onlyWithEmail) rows = rows.filter((r) => !!r.mail_club);

    // tri simple par nom
    rows = [...rows].sort((a, b) =>
      String(a.nom_club || "").localeCompare(String(b.nom_club || ""))
    );

    return rows;
  }, [items, search, onlyWithEmail]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * limit;
  const pageItems = filtered.slice(start, start + limit);

  // helpers sélection
  const toggleSelect = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const visibleIds = useMemo(() => pageItems.map((i) => i.id), [pageItems]);

  const toggleSelectAllVisible = () => {
    const allSelected = visibleIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...visibleIds])]);
    }
  };

  // ======= CRUD =======
  const onCreate = async (payload) => {
    const toSend = { ...payload, user_id: userId };
    try {
      await createInvitedClub(toSend);
      toast.success("Club créé.");
      setCreateOpen(false);
      await fetchClubs();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Impossible de créer le club.");
    }
  };

  const onUpdate = async (id, payload) => {
    try {
      await updateInvitedClub(id, payload); // ← plus de userId ici
      toast.success("Club mis à jour.");
      setEditClub(null);
      await fetchClubs();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Erreur lors de la mise à jour.");
    }
  };

  const onDeleteOne = async (id) => {
    if (!window.confirm("Supprimer ce club ?")) return;
    try {
      await deleteInvitedClub(id);
      toast.success("Club supprimé.");
      await fetchClubs();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Suppression impossible.");
    }
  };

  const onBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Supprimer ${selectedIds.length} club(s) ?`)) return;
    try {
      await bulkDeleteInvitedClubs(selectedIds);
      toast.success("Clubs supprimés.");
      setSelectedIds([]);
      await fetchClubs();
    } catch {
      toast.error("Suppression multiple impossible.");
    }
  };

  const onBulkAssign = () => {
    if (selectedIds.length === 0) {
      toast.info("Sélectionnez au moins un club.");
      return;
    }
    setBulkAssignOpen(true);
  };

  const openManageClub = (club) => setManageClub(club);
  const closeManageClub = () => setManageClub(null);

  return (
    <div className="mx-auto w-full max-w-[1280px] p-3 mt-[130px] dark:text-white">
      <header className="mb-5">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
          Clubs invités
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Gérez votre répertoire, affectez des clubs à vos compétitions et envoyez les invitations.
        </p>
        {user && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            (Organisateur: {userId} - {user.nom} {user.prenom})
          </p>
        )}
      </header>

      {/* Barre d’outils */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher (nom, email, tel)"
              className="w-64 rounded-xl border border-gray-200/70 dark:border-slate-700/70 bg-white/70 dark:bg-slate-800/70 backdrop-blur px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
            />
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={onlyWithEmail}
              onChange={(e) => {
                setPage(1);
                setOnlyWithEmail(e.target.checked);
              }}
            />
            Avec email uniquement
          </label>

          <div className="inline-flex items-center gap-2">
            <select
              value={limit}
              onChange={(e) => {
                setPage(1);
                setLimit(Number(e.target.value));
              }}
              className="rounded-xl border border-gray-200/70 dark:border-slate-700/70 bg-white/70 dark:bg-slate-800/70 px-3 py-2 text-sm"
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}/page
                </option>
              ))}
            </select>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {total} club(s)
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="danger" size="sm" icon={FaTrash} onClick={onBulkDelete}>
            Supprimer sélection
          </Button>
          <Button variant="outline" size="sm" icon={FaShareSquare} onClick={onBulkAssign}>
            Affecter à une compétition
          </Button>
          <Button variant="brand" size="sm" icon={FaPlus} onClick={() => setCreateOpen(true)}>
            Nouveau club
          </Button>
        </div>
      </div>

      {/* Tableau (avec pagination client) */}
      <InvitedClubTable
        items={pageItems}
        loading={loading}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAllVisible}
        allVisibleSelected={visibleIds.every((id) => selectedIds.includes(id))}
        onEdit={(club) => setEditClub(club)}
        onDelete={(club) => onDeleteOne(club.id)}
        onManage={(club) => openManageClub(club)}
        page={currentPage}
        setPage={setPage}
        limit={limit}
        total={total}
      />

      {/* Modales */}
      <CreateInvitedClubModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={onCreate}
        userId={userId}
      />
      {editClub && (
        <EditInvitedClubModal
          open={!!editClub}
          onClose={() => setEditClub(null)}
          club={editClub}
          onSubmit={(payload) => onUpdate(editClub.id, payload)}
        />
      )}
      <BulkAssignModal
        open={bulkAssignOpen}
        onClose={() => setBulkAssignOpen(false)}
        competitions={competitions}
        selectedClubIds={selectedIds}
        onAssign={async ({ competition_id, expires_at }) => {
          try {
            await assignClubsToCompetition({
              competition_id,
              club_ids: selectedIds,
              expires_at,
            });
            toast.success("Clubs affectés à la compétition.");
            setBulkAssignOpen(false);
          } catch {
            toast.error("Affectation impossible.");
          }
        }}
      />
      {manageClub && (
        <ManageClubCompetitionsModal
          open={!!manageClub}
          onClose={closeManageClub}
          club={manageClub}
          competitions={competitions}
          listInvitesByCompetition={listInvitesByCompetition}
          assignOne={async (competition_id) =>
            assignClubsToCompetition({ competition_id, club_ids: [manageClub.id] })
          }
          removeOne={async (inviteId) => deleteInvite(inviteId)}
        />
      )}

      <ToastContainer />
    </div>
  );
};

export default InvitedClubPage;
